import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Car, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const ServicesSection: React.FC = () => {
  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const services = [
    {
      icon: Briefcase,
      title: t("1131", "Business Transfer"),
      description: t(
        "1132",
        "We specialize in professional, seamless transportation solutions for business travelers. Our services are designed to cater to the unique demands of corporate clients, providing the highest level of reliability, comfort, and efficiency to ensure you reach your destination on time and in style."
      ),
      color: "from-primary to-primary-light",
    },
    {
      icon: Car,
      title: t("1133", "Booking Now"),
      description: t(
        "1134",
        "Need reliable and professional transportation for your business trip? Click the BOOK NOW button to reserve your ride quickly and easily. Our team is ready to handle every detail, so you can stay focused on your meetings and objectives."
      ),
      color: "from-accent to-primary-light",
    },
    {
      icon: MapPin,
      title: t("1135", "City Transfers"),
      description: t(
        "1136",
        "Need reliable transport around the city? With our city transport service, you can enjoy smooth, comfortable rides to any destination, from attractions to business appointments. For added convenience, book by the hour or reserve a taxi for the whole day, letting you explore at your own pace."
      ),
      color: "from-warning to-primary",
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,var(--primary)_0%,transparent_50%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,var(--accent)_0%,transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-16"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4"
          >
            <Car className="h-4 w-4" />
            <span>{t("1137", "Our Services")}</span>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6"
          >
            {t("1138", "Premium Transportation")}{" "}
            <span className="relative">
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1139", "Services")}
              </span>
              <motion.div
                className="absolute -bottom-1 left-0 right-0 h-1 bg-linear-to-r from-primary to-primary-light rounded-full"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              />
            </span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-lg text-gray-100 leading-relaxed max-w-3xl mx-auto"
          >
            {t(
              "1140",
              "Everything you need from your taxi business is here! Professional service for every journey."
            )}
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {services.map((service) => (
            <motion.div
              key={service.title}
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              className="group relative bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-700"
            >
              {/* Gradient Background */}
              <div
                className={`absolute inset-0 bg-linear-to-br ${service.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}
              />

              {/* Icon */}
              <motion.div
                className={`inline-flex items-center justify-center w-16 h-16 bg-linear-to-r ${service.color} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}
                whileHover={{ rotate: 5 }}
              >
                <service.icon className="h-8 w-8 text-white" />
              </motion.div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary transition-colors duration-300">
                {service.title}
              </h3>

              <p className="text-gray-200 leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Action Button */}
              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-center text-primary font-semibold group-hover:text-primary-dark transition-colors duration-300"
              >
                <span className="mr-2">{t("1141", "Learn More")}</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.div>

              {/* Decorative Element */}
              <motion.div
                className={`absolute top-4 right-4 w-20 h-20 bg-linear-to-r ${service.color} rounded-full opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-300`}
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link
            to="/services"
            className="group inline-flex items-center bg-linear-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
          >
            <span>{t("1142", "View All Services")}</span>
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
